import React, { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  ArrowRight,
  ChevronRight,
  Shield,
  Zap,
  Trophy,
  Building2,
  Code2,
  Smartphone,
  Database,
  Globe,
  Cpu,
  Brain,
  Users,
  MapPin,
  CheckCircle,
  Star,
  TrendingUp,
} from "lucide-react";
import vibhlogo from "../vibhlogo.png";

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      variants={fade}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      transition={{ delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function StaggerContainer({ children, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      variants={stagger}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const stats = [
  { value: "57+", label: "Projects Delivered" },
  { value: "50+", label: "India Projects" },
  { value: "4", label: "Countries Served" },
  { value: "Oct 2025", label: "Founded" },
];

const coreValues = [
  {
    icon: Zap,
    title: "Innovation First",
    desc: "We architect solutions at the frontier of technology, ensuring your business stays ahead of the curve with modern, scalable software.",
  },
  {
    icon: Trophy,
    title: "Client Success",
    desc: "Your growth is our benchmark. Every line of code we write is measured against the real-world outcomes it creates for your business.",
  },
  {
    icon: Globe,
    title: "Global Reach",
    desc: "Built in Noida, deployed globally. Our solutions power businesses across India, UK, Canada and Cambodia with zero compromise on quality.",
  },
  {
    icon: Shield,
    title: "Transparent Delivery",
    desc: "Milestone-based billing, real-time project tracking, and open communication at every stage — no surprises, ever.",
  },
];

const services = [
  {
    icon: Code2,
    title: "Custom Software",
    desc: "Bespoke enterprise applications engineered for your exact workflows and scale requirements.",
  },
  {
    icon: Smartphone,
    title: "Web & Mobile",
    desc: "Cross-platform web apps and native mobile experiences that convert and retain users.",
  },
  {
    icon: Database,
    title: "ERP & CRM",
    desc: "End-to-end ERP and CRM platforms that unify operations, sales, and customer data.",
  },
  {
    icon: Building2,
    title: "POS Systems",
    desc: "Robust point-of-sale solutions for retail, hospitality, and service-based businesses.",
  },
  {
    icon: TrendingUp,
    title: "Digital Marketing",
    desc: "Data-driven SEO, performance marketing, and content strategies that compound growth.",
  },
  {
    icon: Brain,
    title: "AI & Cloud",
    desc: "AI-powered automation and cloud infrastructure that future-proofs your technology stack.",
  },
];

const whyUs = [
  {
    icon: CheckCircle,
    title: "Milestone-Based Payments",
    desc: "Pay only as verified deliverables are completed — full financial control, zero upfront risk.",
  },
  {
    icon: Zap,
    title: "Agile Delivery",
    desc: "Sprint-based development with weekly demos, keeping you in the loop at every iteration.",
  },
  {
    icon: Shield,
    title: "MCA Registered Entity",
    desc: "Incorporated under the Companies Act 2013 (CIN: U62091UP2025PTC234668) — fully compliant and trustworthy.",
  },
  {
    icon: Star,
    title: "24/7 Dedicated Support",
    desc: "Round-the-clock engineering support post-launch so your systems never go dark.",
  },
];

const directors = [
  {
    name: "Kundan Kumar",
    role: "Co-Founder & Director",
    initials: "KK",
    bio: "Visionary technologist driving product strategy, client partnerships, and engineering excellence at Vibhraad.",
  },
  {
    name: "Jeetendra Yadav",
    role: "Co-Founder & Director",
    initials: "JY",
    bio: "Operations and growth architect ensuring flawless delivery, team scaling, and long-term business sustainability.",
  },
];

export default function AboutUsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white font-sans overflow-x-hidden">

      {/* 1. HERO */}
      <section className="relative bg-gray-950 overflow-hidden min-h-[92vh] flex items-center">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full bg-blue-600/10 blur-[120px]" />
          <div className="absolute top-1/2 -right-60 w-[600px] h-[600px] rounded-full bg-blue-500/8 blur-[100px]" />
          <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] rounded-full bg-indigo-600/8 blur-[80px]" />
        </div>
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 w-full">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-blue-600/15 border border-blue-500/25 rounded-full px-4 py-2 mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-blue-300 text-sm font-semibold tracking-widest uppercase">
                About Vibhraad Technology
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.05] tracking-tight mb-6"
            >
              We Build Software
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-blue-300 to-indigo-400 bg-clip-text text-transparent">
                That Drives Business
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-xl md:text-2xl text-gray-400 leading-relaxed max-w-2xl mb-10"
            >
              Vibhraad Technology Private Limited is an enterprise software
              company incorporated in October 2025, headquartered in Noida,
              India. We engineer scalable, secure, and business-critical software
              for clients across 4 countries — India, UK, Canada &amp; Cambodia.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-200 shadow-lg shadow-blue-600/30 hover:-translate-y-0.5 text-lg"
              >
                Start a Project
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/leadership"
                className="inline-flex items-center gap-2 bg-white/8 hover:bg-white/14 border border-white/15 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-200 text-lg"
              >
                Meet Leadership
                <ChevronRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>

          {/* Floating company info card */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="hidden xl:block absolute right-8 top-1/2 -translate-y-1/2 w-80 bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-7"
          >
            <img src={vibhlogo} alt="Vibhraad Logo" className="h-10 mb-6 brightness-200" />
            <div className="space-y-4">
              <div>
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-widest mb-1">CIN</p>
                <p className="text-white font-mono text-sm">U62091UP2025PTC234668</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-widest mb-1">Incorporated</p>
                <p className="text-white text-sm font-semibold">October 2025</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-widest mb-1">Headquarters</p>
                <p className="text-white text-sm">F-375, Sector-36, Noida</p>
                <p className="text-gray-400 text-xs">Uttar Pradesh 201301</p>
              </div>
                            <div>
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-widest mb-1">Branch</p>
                <p className="text-white text-sm">Hata Bazar, Gorakhpur, Uttar Pradesh 203412</p>
              </div>
              {/* <div>
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-widest mb-1">Also in</p>
                <p className="text-white text-sm">Gorakhpur, UP</p>
              </div> */}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. STATS STRIP */}
      <section className="bg-blue-600 py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
            {stats.map((stat) => (
              <motion.div key={stat.label} variants={fade} className="text-center">
                <p className="text-4xl md:text-5xl font-black text-white mb-2 tracking-tight">{stat.value}</p>
                <p className="text-blue-100 text-sm md:text-base font-semibold uppercase tracking-widest">{stat.label}</p>
              </motion.div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* 3. OUR STORY */}
      <section className="bg-white py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <span className="inline-block text-blue-600 text-sm font-bold uppercase tracking-widest mb-4">Our Story</span>
              <h2 className="text-4xl md:text-5xl font-black text-gray-950 tracking-tight leading-tight mb-6">
                Built for Enterprise.<br />
                <span className="text-blue-600">Delivered with Purpose.</span>
              </h2>
              <div className="space-y-5 text-gray-600 text-lg leading-relaxed">
                <p>
                  Vibhraad Technology Private Limited was incorporated in October 2025 by two engineers with a singular conviction: Indian enterprises deserved world-class software partners who combined deep technical expertise with uncompromising accountability.
                </p>
                <p>
                  Operating from our headquarters in Sector-36, Noida and our branch office in Gorakhpur, we assembled a team of product architects, full-stack engineers, and digital strategists united by one mission — to build software that actually moves the needle for businesses.
                </p>
                <p>
                  In less than a year of operations, we have delivered 57+ projects across 4 countries — India, UK, Canada and Cambodia — ranging from complex ERP systems to university management portals. Every project we take is a long-term partnership, not a transaction.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 font-semibold text-sm px-4 py-2 rounded-full border border-blue-100">
                  <CheckCircle className="w-4 h-4" /> MCA Registered
                </span>
                <span className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 font-semibold text-sm px-4 py-2 rounded-full border border-blue-100">
                  <MapPin className="w-4 h-4" /> Noida + Gorakhpur
                </span>
                <span className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 font-semibold text-sm px-4 py-2 rounded-full border border-blue-100">
                  <Globe className="w-4 h-4" /> 4 Countries Served
                </span>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.15}>
              <div className="bg-gray-950 rounded-3xl p-8 lg:p-10 relative overflow-hidden">
                <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" />
                <div className="relative z-10">
                  <img src={vibhlogo} alt="Vibhraad Technology" className="h-10 mb-8 brightness-200" />
                  <h3 className="text-white font-black text-xl mb-8">Company Information</h3>
                  <div className="space-y-6">
                    {[
                      { label: "Legal Name", value: "Vibhraad Technology Private Limited" },
                      { label: "CIN", value: "U62091UP2025PTC234668", mono: true },
                      { label: "Incorporation", value: "October 2025" },
                      { label: "Registered Office", value: "F-375, Sector-36, Noida, UP 201301" },
                      { label: "Branch Office", value: "Gorakhpur, Uttar Pradesh" },
                      { label: "Directors", value: "Kundan Kumar & Jeetendra Yadav" },
                    ].map((item) => (
                      <div key={item.label} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 border-b border-white/8 pb-5">
                        <span className="text-gray-500 text-xs font-bold uppercase tracking-widest min-w-[140px]">{item.label}</span>
                        <span className={`text-white text-sm font-semibold ${item.mono ? "font-mono" : ""}`}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 4. MISSION & VISION */}
      <section className="bg-gray-50 py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block text-blue-600 text-sm font-bold uppercase tracking-widest mb-4">What Drives Us</span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-950 tracking-tight">Mission & Vision</h2>
          </AnimatedSection>

          <StaggerContainer className="grid md:grid-cols-2 gap-8">
            <motion.div variants={fade} className="bg-gray-950 rounded-3xl p-10 relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-transparent pointer-events-none" />
              <div className="absolute -bottom-12 -right-12 w-48 h-48 bg-blue-600/15 rounded-full blur-3xl pointer-events-none" />
              <div className="relative z-10">
                <div className="w-14 h-14 bg-blue-600/20 border border-blue-500/30 rounded-2xl flex items-center justify-center mb-8">
                  <Cpu className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="text-white font-black text-3xl mb-5 tracking-tight">Our Mission</h3>
                <p className="text-gray-400 text-lg leading-relaxed">
                  To empower businesses of every scale with enterprise-grade software that is reliable, scalable, and built to last — while maintaining absolute transparency and measurable outcomes at every stage of delivery.
                </p>
                <div className="mt-8 h-1 w-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" />
              </div>
            </motion.div>

            <motion.div variants={fade} className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-10 relative overflow-hidden group">
              <div className="absolute -top-12 -left-12 w-48 h-48 bg-white/10 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-60 h-60 bg-indigo-600/30 rounded-full blur-3xl pointer-events-none" />
              <div className="relative z-10">
                <div className="w-14 h-14 bg-white/15 border border-white/25 rounded-2xl flex items-center justify-center mb-8">
                  <TrendingUp className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-white font-black text-3xl mb-5 tracking-tight">Our Vision</h3>
                <p className="text-blue-100 text-lg leading-relaxed">
                  To become the most trusted software engineering partner for growing enterprises globally — recognized not just for the quality of our code, but for the tangible business growth our solutions create for every client we serve.
                </p>
                <div className="mt-8 h-1 w-16 bg-white/40 rounded-full" />
              </div>
            </motion.div>
          </StaggerContainer>
        </div>
      </section>

      {/* 5. CORE VALUES */}
      <section className="bg-white py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block text-blue-600 text-sm font-bold uppercase tracking-widest mb-4">Core Values</span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-950 tracking-tight">The Principles We Build On</h2>
            <p className="text-gray-500 text-xl mt-4 max-w-2xl mx-auto">
              Four values that govern every decision, every deadline, and every line of code we write.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((val) => (
              <motion.div
                key={val.title}
                variants={fade}
                className="group bg-gray-50 hover:bg-gray-950 rounded-3xl p-8 transition-all duration-300 border border-gray-100 hover:border-gray-800"
              >
                <div className="w-12 h-12 bg-blue-600/10 group-hover:bg-blue-600/20 rounded-2xl flex items-center justify-center mb-6 transition-colors duration-300">
                  <val.icon className="w-6 h-6 text-blue-600 group-hover:text-blue-400 transition-colors duration-300" />
                </div>
                <h3 className="text-gray-900 group-hover:text-white font-black text-xl mb-3 transition-colors duration-300">{val.title}</h3>
                <p className="text-gray-500 group-hover:text-gray-400 text-sm leading-relaxed transition-colors duration-300">{val.desc}</p>
              </motion.div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* 6. WHY CHOOSE US */}
      <section className="bg-white py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <span className="inline-block text-blue-600 text-sm font-bold uppercase tracking-widest mb-4">Why Vibhraad</span>
              <h2 className="text-4xl md:text-5xl font-black text-gray-950 tracking-tight leading-tight mb-10">
                The Vibhraad<br />
                <span className="text-blue-600">Advantage</span>
              </h2>
              <div className="space-y-8">
                {whyUs.map((item) => (
                  <div key={item.title} className="flex gap-5">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-50 border border-blue-100 rounded-2xl flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-gray-900 font-black text-lg mb-1">{item.title}</h4>
                      <p className="text-gray-500 text-base leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.15}>
              <div className="bg-blue-600 rounded-3xl p-10 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-56 h-56 bg-white/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-indigo-600/30 rounded-full blur-2xl pointer-events-none" />
                <div className="relative z-10">
                  <h3 className="text-white font-black text-3xl mb-3 tracking-tight">Ready to Build?</h3>
                  <p className="text-blue-100 text-base mb-8 leading-relaxed">
                    Talk to our engineering team today. We scope projects within 48 hours and deliver first milestones in under 2 weeks.
                  </p>
                  <div className="space-y-4 mb-8">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white font-semibold text-sm">Headquarters</p>
                        <p className="text-blue-200 text-sm">F-375, Sector-36, Noida, UP 201301</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white font-semibold text-sm">Branch Office</p>
                        <p className="text-blue-200 text-sm">Gorakhpur, Uttar Pradesh</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white font-semibold text-sm">CIN</p>
                        <p className="text-blue-200 text-sm font-mono">U62091UP2025PTC234668</p>
                      </div>
                    </div>
                  </div>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-white text-blue-600 font-black px-7 py-4 rounded-2xl hover:bg-blue-50 transition-colors duration-200 shadow-lg"
                  >
                    Get in Touch <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 8. LEADERSHIP TEASER */}
      <section className="bg-gray-950 py-28 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 left-1/4 w-[500px] h-[500px] bg-blue-600/8 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-indigo-600/8 rounded-full blur-[80px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block text-blue-400 text-sm font-bold uppercase tracking-widest mb-4">Leadership</span>
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">The Founders</h2>
            <p className="text-gray-400 text-xl mt-4 max-w-xl mx-auto">
              Two engineers. One vision. Builders who have traded comfort for conviction to create something lasting.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {directors.map((dir) => (
              <motion.div
                key={dir.name}
                variants={fade}
                className="bg-white/5 border border-white/10 rounded-3xl p-8 hover:bg-white/8 transition-colors duration-300"
              >
                <div className="flex items-center gap-5 mb-6">
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-600/30">
                    <span className="text-white font-black text-xl">{dir.initials}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-black text-xl">{dir.name}</h3>
                    <p className="text-blue-400 font-semibold text-sm">{dir.role}</p>
                  </div>
                </div>
                <p className="text-gray-400 text-base leading-relaxed mb-6">{dir.bio}</p>
                <Link
                  to="/leadership"
                  className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 font-semibold text-sm transition-colors"
                >
                  Full Profile <ChevronRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </StaggerContainer>

          <AnimatedSection className="text-center mt-12">
            <Link
              to="/leadership"
              className="inline-flex items-center gap-2 bg-white/8 hover:bg-white/14 border border-white/15 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-200"
            >
              View Full Leadership Page <ArrowRight className="w-5 h-5" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

    </div>
  );
}
